(() => {
  if (customElements.get('mw-product-reviews')) return;
  class ProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const opener = this.querySelector('[data-open-review]');
      const wrap = this.querySelector('[data-review-form]');
      const form = wrap?.querySelector('form');
      const closer = this.querySelector('[data-close-review]');
      if (opener && wrap && form) {
        const ratings = Array.from(form.querySelectorAll('input[type="radio"]'));
        let previousOverflow = '';
        let ownsScrollLock = false;
        const paintRating = (value = Number.parseInt(ratings.find(radio => radio.checked)?.value, 10) || 0) => {
          ratings.forEach((radio, index) => {
            const symbol = radio.parentElement.querySelector('[data-rating-symbol]');
            if (symbol) symbol.textContent = index < value ? '★' : '☆';
          });
        };
        const restore = () => {
          opener.setAttribute('aria-expanded', 'false');
          if (ownsScrollLock && !document.querySelector('dialog[open]')) document.documentElement.style.overflow = previousOverflow;
          ownsScrollLock = false;
          if (opener.isConnected) opener.focus({ preventScroll: true });
        };
        const open = () => {
          if (wrap.open) return;
          previousOverflow = document.documentElement.style.overflow;
          wrap.showModal();
          document.documentElement.style.overflow = 'hidden';
          ownsScrollLock = true;
          opener.setAttribute('aria-expanded', 'true');
          wrap.returnFocus = opener;
          wrap.scrollTop = 0;
          paintRating();
          const result = form.querySelector('[data-form-result]');
          if (result) { result.tabIndex = -1; result.focus({ preventScroll: true }); }
          else closer?.focus({ preventScroll: true });
        };
        const close = () => { if (wrap.open) wrap.close(); };
        this.cleanupReviewDialog = () => { close(); restore(); };
        opener.setAttribute('aria-expanded', 'false');
        opener.addEventListener('click', event => { event.preventDefault(); open(); }, options);
        closer?.addEventListener('click', close, options);
        wrap.addEventListener('cancel', event => { event.preventDefault(); event.stopImmediatePropagation(); close(); }, { ...options, capture: true });
        wrap.addEventListener('close', restore, options);
        wrap.addEventListener('click', event => {
          if (event.target !== wrap) return;
          const bounds = wrap.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
            event.preventDefault(); event.stopImmediatePropagation(); close();
          }
        }, { ...options, capture: true });
        ratings.forEach((radio, index) => {
          radio.addEventListener('change', () => paintRating(), options);
          radio.parentElement.querySelector('label')?.addEventListener('mouseenter', () => {
            if (matchMedia('(hover: hover) and (pointer: fine)').matches) paintRating(index + 1);
          }, options);
        });
        form.querySelector('.mw-reviews-rating-options')?.addEventListener('mouseleave', () => paintRating(), options);
        paintRating();
        const ownHash = location.hash === '#' + form.id || location.hash === '#' + wrap.id;
        if (ownHash || form.querySelector('[data-form-result]')) open();
        // Preserve native Shopify validation, spam protection and submission.
      }
      this.setupReviewScopes(options);
    }
    setupReviewScopes(options) {
      const tabs = Array.from(this.querySelectorAll('[data-review-scope]'));
      if (!tabs.length) return;
      const generation = (this.reviewLoadVersion || 0) + 1;
      this.reviewLoadVersion = generation;
      const summary = this.querySelector('[data-review-summary]');
      const status = this.querySelector('[data-review-load-status]');
      const retry = this.querySelector('[data-review-retry]');
      const step = Math.max(2, Math.min(10, Number(this.dataset.initialReviews) || 3));
      const shown = { store: step, product: step };
      let selected = 'store', reviews = [], ready = false, failed = false;
      const make = (tag, className, content) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (content != null) node.textContent = String(content);
        return node;
      };
      const stars = value => {
        const el = make('span', 'mw-review-stars');
        el.setAttribute('role', 'img');
        el.setAttribute('aria-label', `${value} out of 5 stars`);
        el.style.setProperty('--mw-stars-fill', `${value * 20}%`);
        const back = make('span', '', '★★★★★'), fill = make('span', 'mw-review-stars-fill', '★★★★★');
        back.setAttribute('aria-hidden', 'true'); fill.setAttribute('aria-hidden', 'true');
        el.append(back, fill); return el;
      };
      const safeProductUrl = value => {
        try { const url = new URL(value, location.origin); return url.origin === location.origin && /(?:^|\/)products\//.test(url.pathname) ? url.pathname + url.search : null; }
        catch { return null; }
      };
      const normalize = entries => {
        const seen = new Set();
        return entries.filter(review => {
          if (!review || !review.id || seen.has(review.id) || !String(review.author || '').trim() || !String(review.body || '').trim() || !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5 || !review.product_id || !safeProductUrl(review.product_url)) return false;
          seen.add(review.id); return true;
        }).sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || String(a.id).localeCompare(String(b.id)));
      };
      const listFor = scope => scope === 'store' ? reviews : reviews.filter(review => String(review.product_id) === this.dataset.productId);
      const rowFor = (review, scope) => {
        const row = make('article', 'mw-review-row'); row.dataset.reviewRow = ''; row.tabIndex = -1;
        const person = make('div', 'mw-review-person'); person.append(make('p', 'mw-review-author', review.author));
        if (/^\d{4}-\d{2}-\d{2}$/.test(review.date || '')) {
          const date = new Date(review.date + 'T12:00:00Z');
          if (!Number.isNaN(date.valueOf())) { const time = make('time', '', new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(date)); time.dateTime = review.date; person.append(time); }
        }
        const copy = make('div', 'mw-review-copy'); copy.append(stars(review.rating));
        if (review.title) copy.append(make('h3', 'mw-review-title', review.title));
        copy.append(make('p', 'mw-review-body', review.body));
        if (scope === 'store') { const source = make('p', 'mw-review-product-source', 'Reviewed product: '), link = make('a', '', review.product_title || 'View product'); link.href = safeProductUrl(review.product_url); source.append(link); copy.append(source); }
        row.append(person, copy); return row;
      };
      const render = () => {
        summary.replaceChildren();
        for (const scope of ['store', 'product']) {
          const items = ready ? listFor(scope) : [];
          this.querySelector(`[data-scope-count="${scope}"]`).textContent = ready ? String(items.length) : '…';
          const list = this.querySelector(`[data-scope-list="${scope}"]`); list.replaceChildren();
          items.slice(0, shown[scope]).forEach(review => list.append(rowFor(review, scope)));
          this.querySelector(`[data-scope-more="${scope}"]`).hidden = !ready || shown[scope] >= items.length;
          this.querySelector(`[data-scope-less="${scope}"]`).hidden = !ready || Math.min(shown[scope], items.length) <= step;
          this.querySelector(`[data-scope-progress="${scope}"]`).textContent = ready ? `Showing ${Math.min(shown[scope], items.length)} of ${items.length} reviews` : '';
        }
        if (!ready) { summary.append(make('p', 'mw-reviews-loading', failed ? 'Reviews are temporarily unavailable.' : 'Loading reviews…')); return; }
        const items = listFor(selected);
        if (!items.length) {
          const empty = make('div', 'mw-reviews-empty'); empty.append(make('p', 'mw-reviews-empty-title', selected === 'store' ? 'No store reviews yet' : 'No reviews for this product yet'));
          empty.append(make('p', '', selected === 'store' ? 'Customer feedback from products across our store will appear here.' : this.dataset.productEmptyMessage || 'Be the first to share how this piece fits and feels.')); summary.append(empty);
        } else {
          const average = Math.round(items.reduce((total, review) => total + review.rating, 0) / items.length * 10) / 10;
          const rating = make('div', 'mw-reviews-rating'), info = make('div'); info.append(stars(average), make('p', '', `Based on ${items.length} ${items.length === 1 ? 'review' : 'reviews'}`)); rating.append(make('span', 'mw-reviews-average', average.toFixed(1)), info); summary.append(rating);
        }
      };
      const select = scope => {
        selected = scope;
        tabs.forEach(tab => { const active = tab.dataset.reviewScope === scope; tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1; });
        this.querySelectorAll('[data-review-scope-panel]').forEach(panel => { panel.hidden = panel.dataset.reviewScopePanel !== scope; });
        render();
      };
      tabs.forEach(tab => {
        tab.addEventListener('click', () => select(tab.dataset.reviewScope), options);
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault(); const i = tabs.indexOf(tab), next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          select(tabs[next].dataset.reviewScope); tabs[next].focus();
        }, options);
      });
      this.querySelectorAll('[data-scope-more]').forEach(button => button.addEventListener('click', () => {
        const scope = button.dataset.scopeMore, previous = shown[scope]; shown[scope] += step; render(); this.querySelector(`[data-scope-list="${scope}"]`)?.children[previous]?.focus({ preventScroll: true });
      }, options));
      this.querySelectorAll('[data-scope-less]').forEach(button => button.addEventListener('click', () => {
        const scope = button.dataset.scopeLess;
        shown[scope] = step;
        render();
        tabs.find(tab => tab.dataset.reviewScope === scope)?.focus({ preventScroll: true });
        this.querySelector('.mw-reviews-heading')?.scrollIntoView({
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }, options));
      const parse = html => {
        const doc = new DOMParser().parseFromString(html, 'text/html'), node = doc.querySelector('[data-mw-review-feed]');
        if (!node) throw new Error('Review feed missing');
        const value = JSON.parse(node.textContent); if (value.version !== 1 || !Array.isArray(value.reviews) || !Number.isInteger(value.page)) throw new Error('Invalid review feed'); return value;
      };
      const fetchPage = async (page, parameter) => {
        const url = new URL(this.dataset.reviewFeedUrl, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid review endpoint');
        if (page) url.searchParams.set(parameter || 'page', String(page));
        let response;
        for (let attempt = 0; attempt < 3; attempt++) {
          response = await fetch(url, { credentials: 'same-origin', signal: options.signal });
          if (response.status !== 429 && response.status < 500) break;
          if (attempt < 2) await new Promise((resolve, reject) => { const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted','AbortError')); }; const timer = setTimeout(() => { options.signal.removeEventListener('abort', abort); resolve(); }, (attempt + 1) * 1000); options.signal.addEventListener('abort', abort, { once: true }); });
        }
        if (!response.ok) throw new Error('Reviews could not be loaded');
        const feed = parse(await response.text()); if (page && feed.page !== page) throw new Error('Review pagination mismatch'); return feed;
      };
      const load = async refresh => {
        if (this.reviewsLoading) return;
        this.reviewsLoading = true; ready = false; failed = false; retry.hidden = true; status.hidden = false; status.textContent = 'Loading reviews…'; render();
        try {
          let first;
          if (!refresh) { const node = this.querySelector('[data-review-initial-feed] [data-mw-review-feed]'); if (node) first = parse(node.outerHTML); }
          if (!first || first.page !== 1) first = await fetchPage(1, first?.page_param);
          const pages = Math.max(1, Number(first.pages) || 1); if (!Number.isInteger(pages)) throw new Error('Invalid page count');
          const values = [...first.reviews];
          // Load every native page before reporting totals/averages; never silently stop at 50/250 entries.
          for (let page = 2; page <= pages; page += 3) {
            const batch = await Promise.all(Array.from({ length: Math.min(3, pages - page + 1) }, (_, index) => fetchPage(page + index, first.page_param)));
            batch.forEach(feed => values.push(...feed.reviews));
          }
          if (generation !== this.reviewLoadVersion) return;
          reviews = normalize(values); ready = true; status.hidden = true;
        } catch (error) {
          if (error.name === 'AbortError' || generation !== this.reviewLoadVersion) return;
          failed = true; status.textContent = 'Reviews could not be loaded. Please try again.'; retry.hidden = false;
        } finally { if (generation === this.reviewLoadVersion) { this.reviewsLoading = false; render(); } }
      };
      retry.addEventListener('click', () => load(true), options);
      select('store'); load(false);
    }
    disconnectedCallback() {
      this.reviewLoadVersion = (this.reviewLoadVersion || 0) + 1;
      this.reviewsLoading = false;
      this.cleanupReviewDialog?.();
      this.cleanupReviewDialog = null;
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('mw-product-reviews', ProductReviews);
})();
